import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Plus, ExternalLink, Mail, FileDown, Printer, FileText, Pencil, StickyNote } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { can } from '../auth';
import {
  downloadCSV, downloadExcel, downloadTablePDF, printTable, STATUS_LABELS,
} from '../utils/exportHelpers';

function waLink(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  const number = digits.startsWith('0') ? '27' + digits.slice(1) : digits;
  return `https://wa.me/${number}`;
}

function WhatsAppIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

const STATUS_COLORS = {
  created:          'bg-gray-100 text-gray-700',
  collected:        'bg-blue-100 text-blue-700',
  at_depot:         'bg-purple-100 text-purple-700',
  out_for_delivery: 'bg-yellow-100 text-yellow-700',
  delivered:        'bg-green-100 text-green-700',
  failed_delivery:  'bg-red-100 text-red-700',
  returned_to_depot:'bg-orange-100 text-orange-700',
  rescheduled:      'bg-indigo-100 text-indigo-700',
  cancelled:        'bg-red-200 text-red-900',
};

const STATUSES = [
  '', 'created', 'collected', 'at_depot', 'out_for_delivery',
  'delivered', 'failed_delivery', 'returned_to_depot', 'cancelled',
];

// Selectable statuses for the inline dropdown (cancelled is not set here)
const FLOW_STATUSES = [
  'created', 'collected', 'at_depot', 'out_for_delivery',
  'failed_delivery', 'returned_to_depot', 'delivered',
];

// Export column definitions (shared across CSV / Excel / PDF / Print)
const EXPORT_COLUMNS = [
  { header: 'Tracking #',      value: p => p.tracking_number,                                               width: 22, pdfWidth: 28 },
  { header: 'Description',     value: p => p.description || '',                                             width: 28, pdfWidth: 30 },
  { header: 'Sender',          value: p => p.sender_name || '',                                             width: 22, pdfWidth: 26 },
  { header: 'Recipient',       value: p => p.recipient_name || '',                                          width: 22, pdfWidth: 26 },
  { header: 'Recipient Phone', value: p => p.recipient_phone || '',                                         width: 18, pdfWidth: 22 },
  { header: 'Status',          value: p => STATUS_LABELS[p.status] || p.status,                             width: 20, pdfWidth: 24 },
  { header: 'Pickup Driver',   value: p => p.pickup_driver_name || '',                                      width: 18, pdfWidth: 20 },
  { header: 'Delivery Driver', value: p => p.driver_name || '',                                             width: 18, pdfWidth: 20 },
  { header: 'Price (R)',       value: p => p.final_price ? parseFloat(p.final_price).toFixed(2) : '0.00',  width: 12, pdfWidth: 16, align: 'right' },
  { header: 'Created',         value: p => new Date(p.created_at).toLocaleDateString('en-ZA'),              width: 15, pdfWidth: 18 },
  { header: 'Updated',         value: p => new Date(p.updated_at).toLocaleDateString('en-ZA'),              width: 15, pdfWidth: 18 },
];

function ExportBar({ onExport, exporting }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-gray-400 font-medium">Export:</span>
      {[
        { fmt: 'csv',   label: 'CSV',   cls: 'border-gray-300 text-gray-600' },
        { fmt: 'excel', label: 'Excel', cls: 'border-green-300 text-green-700 bg-green-50' },
        { fmt: 'pdf',   label: 'PDF',   cls: 'border-red-300 text-red-700 bg-red-50' },
      ].map(({ fmt, label, cls }) => (
        <button key={fmt} onClick={() => onExport(fmt)} disabled={!!exporting}
          className={`flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:opacity-80 disabled:opacity-40 transition-opacity ${cls}`}>
          <FileDown size={12} /> {exporting === fmt ? '...' : label}
        </button>
      ))}
      <button onClick={() => onExport('print')} disabled={!!exporting}
        className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded-lg hover:opacity-80 disabled:opacity-40">
        <Printer size={12} /> Print
      </button>
    </div>
  );
}

// The parcel's note, in one line, with a pencil that edits it right in the
// list. Saves through the same route the parcel page uses.
function NoteCell({ parcel, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(parcel.notes || '');
  const [busy, setBusy] = useState(false);
  const editable = can('parcels.book');
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/parcels/${parcel.tracking_number}/notes`, { notes: text.trim() });
      toast.success('Note saved');
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the note');
    } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="mt-1" onClick={(e) => e.stopPropagation()}>
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} autoFocus
          placeholder="Fragile, call first, leave at the gate…"
          className="w-full border rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <div className="flex gap-2 mt-1">
          <button onClick={save} disabled={busy} className="bg-orange-500 text-white px-3 py-1 rounded text-[11px] font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
          <button onClick={() => { setEditing(false); setText(parcel.notes || ''); }} className="px-2 py-1 rounded text-[11px] border text-gray-600">Cancel</button>
        </div>
      </div>
    );
  }
  if (!parcel.notes) {
    return editable ? (
      <button onClick={() => setEditing(true)} title="Add a note" className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-300 hover:text-orange-600">
        <StickyNote size={11} /> note
      </button>
    ) : null;
  }
  return (
    <div className="mt-1 flex items-start gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5 max-w-xs" title={parcel.notes}>
      <StickyNote size={11} className="flex-shrink-0 mt-0.5 text-amber-500" />
      <span className="truncate">{parcel.notes}</span>
      {editable && (
        <button onClick={() => { setText(parcel.notes || ''); setEditing(true); }} title="Edit note" className="text-amber-400 hover:text-amber-700 flex-shrink-0">
          <Pencil size={11} />
        </button>
      )}
    </div>
  );
}

export default function Parcels() {
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [from,   setFrom]     = useState('');
  const [to,     setTo]       = useState('');
  const [exporting, setExporting] = useState('');

  const qc = useQueryClient();

  const { data: parcels = [], isLoading } = useQuery({
    queryKey: ['parcels', search, status, from, to],
    queryFn:  () => api.get('/parcels', {
      params: {
        search: search || undefined,
        status: status || undefined,
        from:   from   || undefined,
        to:     to     || undefined,
        limit: 100,
      },
    }).then(r => r.data),
  });

  const { data: drivers = [] } = useQuery({
    queryKey: ['drivers'],
    queryFn:  () => api.get('/users/drivers').then(r => r.data),
  });

  // Quick-assign from the list
  const assignPickup = async (tracking, driver_id) => {
    if (!driver_id) return;
    try {
      await api.patch(`/parcels/${tracking}/assign-pickup`, { driver_id });
      toast.success('Pickup driver set');
      qc.invalidateQueries({ queryKey: ['parcels'] });
    } catch { toast.error('Failed to set pickup driver'); }
  };
  const assignDelivery = async (tracking, driver_id) => {
    if (!driver_id) return;
    try {
      await api.patch(`/parcels/${tracking}/assign`, { driver_id });
      toast.success('Delivery driver assigned — now on their app');
      qc.invalidateQueries({ queryKey: ['parcels'] });
    } catch { toast.error('Failed to set delivery driver'); }
  };

  // Quick status change from the list (records a scan event + notifies client)
  const changeStatus = async (tracking, event_type) => {
    try {
      await api.post(`/parcels/${tracking}/scan`, { event_type });
      toast.success(`Status: ${STATUS_LABELS[event_type] || event_type}`);
      qc.invalidateQueries({ queryKey: ['parcels'] });
    } catch { toast.error('Failed to change status'); }
  };

  const handleExport = async (fmt) => {
    setExporting(fmt);
    try {
      const data = await api.get('/parcels/export', {
        params: {
          search: search || undefined,
          status: status || undefined,
          from:   from   || undefined,
          to:     to     || undefined,
        },
      }).then(r => r.data);

      if (data.length === 0) { toast.error('No parcels match the current filters'); return; }

      const dateTag  = from || to ? `_${from || 'start'}_to_${to || 'today'}` : '';
      const statusTag = status ? `_${status}` : '';
      const base     = `parcels${statusTag}${dateTag}`;
      const sub      = [
        status ? STATUS_LABELS[status] : 'All statuses',
        from || to ? `${from || '…'} – ${to || '…'}` : '',
      ].filter(Boolean).join('  •  ');

      if (fmt === 'csv')   downloadCSV(data, EXPORT_COLUMNS, `${base}.csv`);
      if (fmt === 'excel') downloadExcel(data, EXPORT_COLUMNS, `${base}.xlsx`, 'Parcels');
      if (fmt === 'pdf')   downloadTablePDF(data, EXPORT_COLUMNS, `${base}.pdf`, 'Parcels Export', sub);
      if (fmt === 'print') printTable(data, EXPORT_COLUMNS, 'Parcels', sub);

      if (fmt !== 'print') toast.success(`${data.length} records exported`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">Parcels</h1>
        <Link to="/parcels/new"
          className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-lg">
          <Plus size={16} /> New Parcel
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search tracking, client…"
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
            {STATUSES.map(s => (
              <option key={s} value={s}>{s ? STATUS_LABELS[s] : 'All statuses'}</option>
            ))}
          </select>
        </div>

        {/* Date range row */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-gray-500 font-medium">Created:</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
          <span className="text-gray-400 text-xs">to</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
          {(from || to) && (
            <button onClick={() => { setFrom(''); setTo(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline">clear</button>
          )}
          <div className="ml-auto">
            <ExportBar onExport={handleExport} exporting={exporting} />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : parcels.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No parcels found</div>
        ) : (
          <>
          {/* Phone: one card per parcel — the same controls as the table below */}
          <div className="md:hidden divide-y">
            {parcels.map(p => (
              <div key={p.id} className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/parcels/${p.tracking_number}`} className="font-mono font-semibold text-gray-800 hover:text-orange-600">
                    {p.tracking_number}
                    {p.box_count > 1 && <span className="ml-1.5 text-[10px] font-sans bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5">{p.box_count} boxes</span>}
                  </Link>
                  <select value={p.status} onChange={e => changeStatus(p.tracking_number, e.target.value)} disabled={p.status === 'cancelled'}
                    className={`border-0 rounded-full px-2.5 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-orange-400 ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-700'}`}>
                    {(FLOW_STATUSES.includes(p.status) ? FLOW_STATUSES : [p.status, ...FLOW_STATUSES]).map(s => (
                      <option key={s} value={s} className="bg-white text-gray-800">{STATUS_LABELS[s] || s}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="min-w-0">
                    {p.sender_name && <span className="text-xs text-gray-500">{p.sender_name} → </span>}
                    <span className="font-medium text-gray-800">{p.recipient_name}</span>
                    {p.recipient_phone && (
                      <span className="ml-2 inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <a href={waLink(p.recipient_phone)} target="_blank" rel="noreferrer" className="text-green-600"><WhatsAppIcon size={14} /></a>
                        <a href={`tel:${p.recipient_phone}`} className="hover:underline">{p.recipient_phone}</a>
                      </span>
                    )}
                  </span>
                  <span className="text-gray-600 whitespace-nowrap">R{parseFloat(p.final_price || 0).toFixed(2)}</span>
                </div>
                <NoteCell parcel={p} onSaved={() => qc.invalidateQueries({ queryKey: ['parcels'] })} />
                <div className="grid grid-cols-2 gap-2">
                  <select value={p.pickup_driver_id || ''} onChange={e => assignPickup(p.tracking_number, e.target.value)}
                    disabled={['delivered', 'cancelled'].includes(p.status)}
                    className="border rounded-lg px-2 py-2 text-xs text-gray-700 bg-white disabled:opacity-50">
                    <option value="">— pickup —</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <select value={p.assigned_driver_id || ''} onChange={e => assignDelivery(p.tracking_number, e.target.value)}
                    disabled={['delivered', 'cancelled'].includes(p.status)}
                    className="border rounded-lg px-2 py-2 text-xs text-gray-700 bg-white disabled:opacity-50">
                    <option value="">— delivery —</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <p className="text-[11px] text-gray-400">Updated {new Date(p.updated_at).toLocaleDateString('en-ZA')}</p>
              </div>
            ))}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Tracking</th>
                  <th className="px-4 py-3 text-left">Recipient</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Pickup Driver</th>
                  <th className="px-4 py-3 text-left">Delivery Driver</th>
                  <th className="px-4 py-3 text-left">Price</th>
                  <th className="px-4 py-3 text-left">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {parcels.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-semibold text-gray-800">
                      {p.tracking_number}
                      {p.box_count > 1 && (
                        // A part-scanned consignment is the thing to notice: a box
                        // still on the van means the pickup is not really done.
                        // The pickup only advances once every box has, so any box
                        // NOT sitting at the pickup's status is one that moved on.
                        (() => {
                          const ahead = p.box_count - p.boxes_at_status;
                          return (
                            <span className={`ml-2 text-[10px] font-sans rounded-full px-1.5 py-0.5 align-middle ${
                              ahead > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {ahead > 0
                                ? `${ahead} of ${p.box_count} boxes scanned`
                                : `${p.box_count} boxes`}
                            </span>
                          );
                        })()
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{p.recipient_name}</p>
                      {p.sender_name && <p className="text-xs text-gray-500">from {p.sender_name}</p>}
                      <div className="flex items-center gap-2 mt-0.5">
                        {p.recipient_phone && (
                          <a href={waLink(p.recipient_phone)} target="_blank" rel="noreferrer"
                            title={`WhatsApp ${p.recipient_phone}`} className="text-green-600 hover:text-green-700">
                            <WhatsAppIcon size={14} />
                          </a>
                        )}
                        {p.recipient_email && (
                          <a href={`mailto:${p.recipient_email}`} title={`Email ${p.recipient_email}`}
                            className="text-blue-500 hover:text-blue-600">
                            <Mail size={13} />
                          </a>
                        )}
                        <span className="text-gray-400 text-xs">{p.recipient_phone}</span>
                      </div>
                      <NoteCell parcel={p} onSaved={() => qc.invalidateQueries({ queryKey: ['parcels'] })} />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={p.status}
                        onChange={e => changeStatus(p.tracking_number, e.target.value)}
                        disabled={p.status === 'cancelled'}
                        className={`border-0 rounded-full px-2.5 py-1 text-xs font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:cursor-default ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-700'}`}
                      >
                        {(FLOW_STATUSES.includes(p.status) ? FLOW_STATUSES : [p.status, ...FLOW_STATUSES]).map(s => (
                          <option key={s} value={s} className="bg-white text-gray-800">{STATUS_LABELS[s] || s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={p.pickup_driver_id || ''}
                        onChange={e => assignPickup(p.tracking_number, e.target.value)}
                        disabled={['delivered', 'cancelled'].includes(p.status)}
                        className="border rounded-lg px-2 py-1 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
                      >
                        <option value="">— pickup —</option>
                        {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={p.assigned_driver_id || ''}
                        onChange={e => assignDelivery(p.tracking_number, e.target.value)}
                        disabled={['delivered', 'cancelled'].includes(p.status)}
                        className="border rounded-lg px-2 py-1 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
                      >
                        <option value="">— delivery —</option>
                        {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-gray-600">R{parseFloat(p.final_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-gray-400">{new Date(p.updated_at).toLocaleDateString('en-ZA')}</td>
                    <td className="px-4 py-3">
                      <Link to={`/parcels/${p.tracking_number}`} className="text-orange-500 hover:text-orange-600">
                        <ExternalLink size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {parcels.length >= 100 && (
            <p className="text-xs text-center text-gray-400 py-2">
              Showing first 100 results — use date/status filters to narrow down, or Export to get all matching records.
            </p>
          )}
          </>
        )}
      </div>
    </div>
  );
}
